/**
 * 착수계 작성/수정 폼 모달 — 납품계 폼과 동일한 흐름.
 *
 *   [작성]
 *     1. 분할납품요구서 PDF 업로드 → 자동 추출(사업명·수요기관·금액·날짜·품목·물품식별번호)
 *     2. 제출일(시행일=착수일) 입력(필수) → 문서번호 자동 제안 (납품계와 공용 대장)
 *     3. 자동 채움 필드 검토·수정 + 직인 포함 여부
 *     4. [생성] → 9종 양식 PDF 생성 → 미리보기
 *   [수정] editId 지정 시 기존 값 불러와 수정 → 저장.
 *
 * 납품기한(=준공년월일)은 예정공정표 날짜 계산에 필요해 필수.
 */

import { useCallback, useEffect, useState } from 'react';
import { DraggableModal } from '../../components/DraggableModal';
import { DateInput } from '../../components/DateInput';
import { useDirtyClose } from '../../lib/useDirtyClose';
import { parseBunapFile, getNextDocNo } from '../../lib/delivery-api';
import {
  createCommencement, getCommencement, updateCommencement,
  type CommencementCreateInput, type CommencementUpdateInput,
} from '../../lib/commencement-api';
import {
  AttachmentListEditor, CHAKSU_DEFAULT_ATTACHMENTS, withKeys, type DocAttachment,
} from './AttachmentListEditor';
import { LoadingLines } from '../../components/ui/Skeleton';

interface Props {
  editId?: number | null;
  onClose: () => void;
  onSaved: (id: number, docNo: string) => void;
}

interface FormState {
  businessName: string;
  recipient: string;
  department: string;
  procurementNo: string;
  contractDate: string;
  deliveryDue: string;
  amount: string;
  itemName: string;
  spec: string;
  identNo: string;
  unit: string;
  qty: string;
}

const EMPTY: FormState = {
  businessName: '', recipient: '', department: '', procurementNo: '',
  contractDate: '', deliveryDue: '', amount: '', itemName: '', spec: '', identNo: '', unit: '대', qty: '1',
};

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-\d{2}$/; // 년-월까지만 (DateInput allowMonthOnly · v2.322.0)

export function CommencementFormModal({ editId, onClose, onSaved }: Props) {
  // 작성 중 닫힘 방지 — ESC·[×]·[취소] 는 전부 requestClose 를 거친다 (v2.163.0)
  const { markDirty, requestClose } = useDirtyClose(onClose);
  const isEdit = editId != null;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [bunapFile, setBunapFile] = useState<File | null>(null);
  // 제출일(시행일) = 착수년월일 = 각 서식 날짜·문서번호 기준 (필수)
  const [submitDate, setSubmitDate] = useState('');
  const [includeSeal, setIncludeSeal] = useState(true);
  const [attachments, setAttachments] = useState<DocAttachment[]>(CHAKSU_DEFAULT_ATTACHMENTS);
  const [docNo, setDocNo] = useState('');
  const [docNoTouched, setDocNoTouched] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [parsedNote, setParsedNote] = useState('');

  const set = (k: keyof FormState) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  // 수정 모드 — 기존 값 불러오기
  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    (async () => {
      try {
        const d = await getCommencement(editId!);
        if (!alive) return;
        setForm({
          businessName: d.businessName ?? '',
          recipient: d.recipient ?? '',
          department: d.department ?? '',
          procurementNo: d.procurementNo ?? '',
          contractDate: d.contractDate ?? '',
          deliveryDue: d.deliveryDue ?? '',
          amount: d.amount != null ? String(d.amount) : '',
          itemName: d.itemName ?? '',
          spec: d.spec ?? '',
          identNo: d.identNo ?? '',
          unit: d.unit ?? '대',
          qty: d.qty != null ? String(d.qty) : '1',
        });
        setSubmitDate(d.issueDate ?? '');
        setIncludeSeal(d.includeSeal !== false);
        {
          const baseAtt = d.attachments && d.attachments.length > 0
            ? withKeys(d.attachments, CHAKSU_DEFAULT_ATTACHMENTS)   // 옛 자료에 표식 되붙이기 (v2.179.0)
            : CHAKSU_DEFAULT_ATTACHMENTS;
          setAttachments(baseAtt.map((a, i) => {
            const ef = d.extraFiles?.find((f) => f.sort === i);
            return ef ? { ...a, existingFileId: ef.id, existingFileName: ef.filename } : a;
          }));
        }
        setDocNo(d.docNo ?? '');
        setDocNoTouched(true);
      } catch (e) {
        if (alive) setError(`불러오기 실패: ${(e as Error).message}`);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isEdit, editId]);

  const onPickBunap = useCallback(async (file: File | null) => {
    setBunapFile(file);
    setError('');
    setParsedNote('');
    if (!file) return;
    setParsing(true);
    try {
      const r = await parseBunapFile(file);
      setForm((f) => ({
        ...f,
        businessName: r.businessName ?? f.businessName,
        recipient: r.endUser ?? f.recipient,
        department: r.department ?? f.department,
        procurementNo: r.procurementNo ?? f.procurementNo,
        contractDate: r.requestDate ?? f.contractDate,
        deliveryDue: r.deliveryDue ?? f.deliveryDue,
        amount: r.totalAmount != null ? String(r.totalAmount) : f.amount,
        itemName: r.itemName ?? f.itemName,
        spec: r.spec ?? f.spec,
        identNo: r.identNo ?? f.identNo,
        unit: r.unit ?? f.unit,
        qty: r.qty != null ? String(r.qty) : f.qty,
      }));
      setParsedNote('분납서에서 자동으로 채웠습니다. 내용을 확인·수정해 주세요.');
    } catch (e) {
      setError(`분납서 자동 추출 실패: ${(e as Error).message} (직접 입력 가능)`);
    } finally {
      setParsing(false);
    }
  }, []);

  // 제출일 입력 시 문서번호 자동 제안 (납품계·착수계 공용 대장)
  const onChangeSubmitDate = useCallback(async (v: string) => {
    setSubmitDate(v);
    // 년-월까지만(2026-11) 적어도 번호를 제안한다 — 번호는 그 달(2611-…) (v2.322.0 · 개발요청 #32)
    if (v && (ISO_RE.test(v) || MONTH_RE.test(v)) && !docNoTouched) {
      try {
        const { docNo: suggested } = await getNextDocNo(v);
        setDocNo(suggested);
      } catch { /* 제안 실패 시 무시 — 수동 입력 가능 */ }
    }
  }, [docNoTouched]);

  async function submit() {
    setError('');
    if (!isEdit && !bunapFile) { setError('분할납품요구서 PDF 를 업로드해 주세요.'); return; }
    if (!submitDate) { setError('제출일(시행일=착수일)을 입력해 주세요.'); return; }
    if (!form.businessName.trim()) { setError('사업명(공사명)을 입력해 주세요.'); return; }
    if (!form.recipient.trim()) { setError('수요기관을 입력해 주세요.'); return; }
    if (!form.deliveryDue) { setError('납품기한(준공년월일)을 입력해 주세요 — 예정공정표 날짜 계산에 필요합니다.'); return; }

    const payload = {
      docNo: docNo || undefined,
      issueDate: submitDate,
      recipient: form.recipient.trim(),
      department: form.department.trim(),
      businessName: form.businessName.trim(),
      procurementNo: form.procurementNo.trim(),
      contractDate: form.contractDate || undefined,
      deliveryDue: form.deliveryDue,
      amount: Number(form.amount.replace(/,/g, '')) || 0,
      itemName: form.itemName.trim(),
      spec: form.spec.trim(),
      identNo: form.identNo.trim(),
      unit: form.unit.trim() || '대',
      qty: Number(form.qty) || 1,
      includeSeal,
      attachments: attachments.filter((a) => a.name.trim()),
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await updateCommencement(editId!, payload as CommencementUpdateInput);
        onSaved(editId!, payload.docNo ?? docNo);
      } else {
        const { id, docNo: savedNo } = await createCommencement(payload as CommencementCreateInput, bunapFile!);
        onSaved(id, savedNo);
      }
    } catch (e) {
      setError(`${isEdit ? '수정' : '생성'} 실패: ${(e as Error).message}`);
      setSubmitting(false);
    }
  }

  return (
    <DraggableModal onClose={requestClose} initialWidth={760} closeOnBackdrop={false} closeOnEsc={false} zIndex={60}>
      <div className="dm-drag-handle cursor-move flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
        <h2 className="ui-modal-title">{isEdit ? '착수계 수정' : '착수계 작성'}</h2>
        <button type="button" onClick={requestClose} className="ui-close">×</button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-slate-400 text-sm"><LoadingLines /></div>
      ) : (
      <div className="px-6 py-4 space-y-5" onInput={markDirty} onChange={markDirty}>
        {/* 1. 분납서 업로드 (작성 시 필수 / 수정 시엔 원본 유지) */}
        {!isEdit ? (
          <section>
            <h3 className="ui-subsection-title mb-1">① 분할납품요구서 (PDF)</h3>
            <input
              type="file" accept="application/pdf,.pdf"
              onChange={(e) => onPickBunap(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-600 file:text-white file:text-sm hover:file:bg-blue-700"
            />
            {parsing && <p className="text-xs text-blue-600 mt-1">분납서 분석 중…</p>}
            {parsedNote && <p className="text-xs text-emerald-600 mt-1">✓ {parsedNote}</p>}
          </section>
        ) : (
          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
            수정 모드 — 첨부된 분할납품요구서 원본은 그대로 유지됩니다. 아래 내용·직인을 수정할 수 있습니다.
          </p>
        )}

        {/* 2. 제출일(시행일=착수일) + 문서번호 */}
        <section>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="ui-label block mb-1">② 제출일(시행일 = 착수일) <span className="text-rose-500">*</span></label>
              <DateInput value={submitDate} onChange={onChangeSubmitDate} allowMonthOnly />
              <p className="text-xs text-slate-500 mt-1">공문 시행일자·착수년월일·각 서식 제출일 · 숫자 「6-1」 입력 후 Enter · 월까지만이면 「2026-11」</p>
            </div>
            <div>
              <label className="ui-label block mb-1">문서번호</label>
              <input type="text" value={docNo} placeholder="예: 2603-SM-005"
                onChange={(e) => { setDocNo(e.target.value); setDocNoTouched(true); }}
                className="ui-input w-full font-mono" />
              <p className="text-xs text-slate-500 mt-1">제출일 입력 시 자동 제안 — 납품계와 이어지는 일련번호</p>
            </div>
          </div>
        </section>

        {/* 3. 자동 채움 필드 */}
        <section>
          <h3 className="ui-subsection-title mb-2">③ 착수계 내용 {isEdit ? '(수정)' : '(분납서에서 자동 채움 — 확인·수정)'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="사업명(공사명)" value={form.businessName} onChange={set('businessName')} colSpan2 required />
            <Field label="수요기관" value={form.recipient} onChange={set('recipient')} required />
            <Field label="참조 부서" value={form.department} onChange={set('department')} />
            <Field label="납품요구번호" value={form.procurementNo} onChange={set('procurementNo')} mono />
            <Field label="계약금액(품대계)" value={form.amount} onChange={set('amount')} mono />
            <DateField label="계약일(납품요구일)" value={form.contractDate} onChange={set('contractDate')} />
            <DateField label="납품기한(준공년월일)" value={form.deliveryDue} onChange={set('deliveryDue')} required monthOnly />
            <Field label="품명" value={form.itemName} onChange={set('itemName')} />
            <Field label="물품식별번호" value={form.identNo} onChange={set('identNo')} mono />
            <Field label="규격" value={form.spec} onChange={set('spec')} colSpan2 />
            <Field label="단위" value={form.unit} onChange={set('unit')} />
            <Field label="수량" value={form.qty} onChange={set('qty')} mono />
          </div>
        </section>

        {/* 4. 직인 */}
        <section>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={includeSeal} onChange={(e) => setIncludeSeal(e.target.checked)} />
            <span className="font-semibold">직인(법인 인감) 넣기</span>
            <span className="text-xs text-slate-500">— 공문·착수계·현장대리인계·재직증명서·공정표·보안서약서의 도장</span>
          </label>
        </section>

        {/* 5. 붙임서류 */}
        <section>
          <h3 className="ui-subsection-title mb-1">붙임서류 <span className="text-slate-500 font-normal text-xs">(표지 공문에 표시 + 만들 서류 고르기 — 줄을 빼면 그 서류가 PDF 에서도 빠집니다)</span></h3>
          <AttachmentListEditor
            items={attachments}
            onChange={setAttachments}
            onReset={() => setAttachments(CHAKSU_DEFAULT_ATTACHMENTS)}
          />
        </section>

        {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}
      </div>
      )}

      <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2 sticky bottom-0">
        <button type="button" onClick={requestClose} className="btn btn-secondary">취소</button>
        <button type="button" onClick={submit} disabled={submitting || parsing || loading}
          className="btn btn-primary">
          {submitting ? (isEdit ? '저장 중…' : '생성 중…') : (isEdit ? '수정 저장' : '착수계 생성')}
        </button>
      </div>
    </DraggableModal>
  );
}

function Field({
  label, value, onChange, mono, required, colSpan2,
}: {
  label: string; value: string; onChange: (v: string) => void;
  mono?: boolean; required?: boolean; colSpan2?: boolean;
}) {
  return (
    <div className={colSpan2 ? 'col-span-2' : ''}>
      <label className="ui-label block mb-1">
        {label}{required && <span className="text-rose-500"> *</span>}
      </label>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)}
        className={`w-full border border-slate-300 rounded px-2 py-1.5 text-sm ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );
}

// monthOnly = 년-월만 받는 칸(납품기한=준공년월일) — 값은 그대로 YYYY-MM-DD 다(그 달 말일).
// ⚠날짜를 버리면 안 된다: 이 값으로 공사기간(착수~준공)과 예정공정표 간트 7등분을 계산한다.
function DateField({ label, value, onChange, required, monthOnly }: {
  label: string; value: string; onChange: (v: string) => void; required?: boolean; monthOnly?: boolean;
}) {
  return (
    <div>
      <label className="ui-label block mb-1">
        {label}{required && <span className="text-rose-500"> *</span>}
      </label>
      <DateInput value={value} onChange={onChange} allowMonthOnly={monthOnly} />
    </div>
  );
}
